import { validateArizonaPostalCode } from "./arizona-postal-code.js";

export const ACCOUNT_SESSION_TIMEOUT_MS = 2000;

const NORMALIZED_POSTAL_CODE_PATTERN = /^\d{5}$/;
const SIGNED_OUT_SESSION = Object.freeze({ authenticated: false, email: "", postalCode: "" });

export function accountPostalCode(account) {
  const postalCode = String(account?.profile?.postal_code || "").trim();
  const validation = validateArizonaPostalCode(postalCode);
  return validation.ok ? validation.value : "";
}

export function shouldUseAccountPostalCode({ authenticated, postalCode, subject }) {
  return subject === "self" && authenticated === true && NORMALIZED_POSTAL_CODE_PATTERN.test(postalCode)
    && validateArizonaPostalCode(postalCode).ok;
}

export function selfReportPostalCodeForAccountSession({
  authenticated,
  postalCode,
  currentPostalCode = "",
  usesAccountPostalCode = false,
}) {
  if (shouldUseAccountPostalCode({ authenticated, postalCode, subject: "self" })) {
    return {
      postalCode: usesAccountPostalCode || !currentPostalCode ? postalCode : currentPostalCode,
      usesAccountPostalCode: usesAccountPostalCode || !currentPostalCode,
    };
  }
  return {
    postalCode: usesAccountPostalCode ? "" : currentPostalCode,
    usesAccountPostalCode: false,
  };
}

export function createAccountSessionRequestGuard() {
  let generation = 0;
  return {
    begin() {
      const requestGeneration = ++generation;
      return () => requestGeneration === generation;
    },
    invalidate() {
      generation += 1;
    },
  };
}

export async function reconcileAccountSessionFlow({
  session,
  applySession,
  restorePendingReport,
  enrichSelfReport,
  refreshRoute,
}) {
  applySession(session);
  await restorePendingReport();
  enrichSelfReport();
  refreshRoute();
}

export async function accountSessionDetails(
  fetchImpl,
  url,
  timeoutMs = ACCOUNT_SESSION_TIMEOUT_MS,
  lateSessionResolved = () => {},
  lateSessionIsCurrent = () => true
) {
  let timeoutId;
  let timedOut = false;
  const timeoutReached = new Promise((resolve) => {
    timeoutId = globalThis.setTimeout(() => {
      timedOut = true;
      resolve(SIGNED_OUT_SESSION);
    }, timeoutMs);
  });
  const requested = Promise.resolve()
    .then(() => fetchImpl(url, {
      credentials: "same-origin",
    }))
    .then(async (response) => {
      if (!response.ok) return SIGNED_OUT_SESSION;
      const payload = await response.json();
      const email = String(payload?.account?.email || "").trim().toLowerCase();
      if (payload?.authenticated !== true || !email) return SIGNED_OUT_SESSION;
      return {
        authenticated: true,
        email,
        postalCode: accountPostalCode(payload.account),
      };
    })
    .then((session) => {
      if (timedOut && session.authenticated && lateSessionIsCurrent()) {
        Promise.resolve(lateSessionResolved(session)).catch(() => {});
      }
      return session;
    })
    .catch(() => SIGNED_OUT_SESSION);

  try {
    return await Promise.race([requested, timeoutReached]);
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

export async function accountSessionIsAuthenticated(fetchImpl, url, timeoutMs = ACCOUNT_SESSION_TIMEOUT_MS) {
  return (await accountSessionDetails(fetchImpl, url, timeoutMs)).authenticated;
}

export function initializeReportingBeforeAccountCheck({
  initializeReporting,
  resolveAccountSession,
  accountSessionResolved,
}) {
  initializeReporting();
  return Promise.resolve()
    .then(resolveAccountSession)
    .then(accountSessionResolved);
}
